import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogContent, DialogActions, Button, IconButton,
    TextField, InputAdornment, Tooltip, CircularProgress,
    Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import moment from 'moment';
import useAxios from '../../helpers/Hooks/useAxios';
import { URLS } from '../../helpers/URLS';
import '../../assets/scss/tables.scss';

const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0] ? parts[0][0] : '') +
        (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const AISummaryModal = ({ open, onClose, searchParameters }) => {
    const { fetchData } = useAxios();

    const [loading, setLoading] = useState(true);
    const [summaryData, setSummaryData] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'individual'
    const [search, setSearch] = useState('');
    const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

    const handleDownloadClick = (e) => setDownloadAnchorEl(e.currentTarget);
    const handleDownloadClose = () => setDownloadAnchorEl(null);

    const startDateStr = searchParameters?.startDate && moment(searchParameters.startDate).isValid()
        ? moment(searchParameters.startDate).format('YYYY-MM-DD') : '';
    const endDateStr = searchParameters?.endDate && moment(searchParameters.endDate).isValid()
        ? moment(searchParameters.endDate).format('YYYY-MM-DD') : '';

    const displayDateRange = useMemo(() => {
        const s = searchParameters?.startDate && moment(searchParameters.startDate).isValid()
            ? moment(searchParameters.startDate).format('DD MMM YYYY') : '';
        const e = searchParameters?.endDate && moment(searchParameters.endDate).isValid()
            ? moment(searchParameters.endDate).format('DD MMM YYYY') : '';
        return s && e ? `${s} – ${e}` : 'Selected Period';
    }, [searchParameters]);

    const fetchSummary = async () => {
        setLoading(true);
        setErrorMsg('');
        try {
            const resp = await fetchData({
                url: `${URLS.getTeamAISummary}?startDate=${startDateStr}&endDate=${endDateStr}`,
                method: 'get'
            });

            if (resp && resp.status === 200 && resp.data) {
                setSummaryData(resp.data);
            } else {
                const message = (resp && (resp.message || resp.error)) || 'Failed to generate AI summary.';
                setErrorMsg(message);
            }
        } catch (err) {
            setErrorMsg(err?.message || 'Something went wrong while contacting the server.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchSummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const filteredMembers = useMemo(() => {
        if (!summaryData?.members) return [];
        return summaryData.members.filter(m => {
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            return (
                String(m.employeeName || '').toLowerCase().includes(q) ||
                String(m.employeeCode || '').toLowerCase().includes(q) ||
                String(m.designation || '').toLowerCase().includes(q)
            );
        });
    }, [summaryData, search]);

    const totalLoggedHours = useMemo(() => {
        if (!summaryData?.members) return 0;
        return summaryData.members.reduce((sum, m) => sum + Number(m.totalHours || 0), 0);
    }, [summaryData]);

    const counts = useMemo(() => {
        const members = summaryData?.members || [];
        return {
            all: members.length,
            reported: members.filter(m => m.hasReported).length,
            missing: members.filter(m => !m.hasReported).length
        };
    }, [summaryData]);

    const generateReportHtml = () => {
        if (!summaryData) return '';
        const members = summaryData.members || [];
        let memberHtml = '';

        members.forEach((m, idx) => {
            memberHtml += `
            <div style="margin-bottom: 20px; padding: 16px; border: 1px solid #cbd5e1; border-radius: 8px; background-color: #ffffff; page-break-inside: avoid;">
                <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-bottom: 10px;">
                    <div>
                        <h3 style="margin: 0; color: #1c4f86; font-size: 15px;">${idx + 1}. ${m.employeeName || 'Unknown'}</h3>
                        <div style="color: #64748b; font-size: 12px;">${m.designation || 'Team Member'} | Code: ${m.employeeCode || 'N/A'}</div>
                    </div>
                    <div style="text-align: right; font-size: 12px; font-weight: 600; color: #0369a1;">
                        ${m.totalHours || 0} hrs logged &bull; ${m.daysReported || 0} days
                    </div>
                </div>
                ${m.executiveOverview ? `<p style="font-style: italic; color: #334155; margin: 8px 0 12px; background: #f8fafc; padding: 8px 12px; border-left: 3px solid #00a88f; border-radius: 4px; font-size: 13px;">"${m.executiveOverview}"</p>` : ''}
                
                ${m.tasksCompleted && m.tasksCompleted.length ? `
                    <div style="margin-bottom: 8px;">
                        <strong style="color: #166534; font-size: 13px;">Tasks Completed:</strong>
                        <ul style="margin: 4px 0 8px; padding-left: 20px; color: #14532d; font-size: 12.5px;">
                            ${m.tasksCompleted.map(t => `<li style="margin-bottom: 3px;">${t}</li>`).join('')}
                        </ul>
                    </div>
                ` : ''}

                ${m.workInProgress && m.workInProgress.length ? `
                    <div style="margin-bottom: 8px;">
                        <strong style="color: #1e40af; font-size: 13px;">Work / Progress Made:</strong>
                        <ul style="margin: 4px 0 8px; padding-left: 20px; color: #1e3a8a; font-size: 12.5px;">
                            ${m.workInProgress.map(w => `<li style="margin-bottom: 3px;">${w}</li>`).join('')}
                        </ul>
                    </div>
                ` : ''}

                ${m.importantContributions && m.importantContributions.length ? `
                    <div style="margin-bottom: 8px;">
                        <strong style="color: #854d0e; font-size: 13px;">Important Contributions:</strong>
                        <ul style="margin: 4px 0 8px; padding-left: 20px; color: #713f12; font-size: 12.5px;">
                            ${m.importantContributions.map(c => `<li style="margin-bottom: 3px;">${c}</li>`).join('')}
                        </ul>
                    </div>
                ` : ''}

                ${m.observations && m.observations.length ? `
                    <div>
                        <strong style="color: #475569; font-size: 13px;">Manager Observations:</strong>
                        <ul style="margin: 4px 0 4px; padding-left: 20px; color: #334155; font-size: 12.5px;">
                            ${m.observations.map(o => `<li style="margin-bottom: 3px;">${o}</li>`).join('')}
                        </ul>
                    </div>
                ` : ''}
            </div>
            `;
        });

        return `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="utf-8">
            <title>WorkHub AI Team Summary - ${displayDateRange}</title>
            <style>
                @media print {
                    @page { margin: 1.5cm; }
                    body { font-size: 11pt; background: #ffffff !important; }
                    .no-print { display: none !important; }
                }
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                    color: #1e293b;
                    line-height: 1.5;
                    padding: 24px;
                    max-width: 900px;
                    margin: 0 auto;
                    background-color: #f8fafc;
                }
                .header-box {
                    background: linear-gradient(135deg, #1c4f86, #00a88f);
                    color: #ffffff;
                    padding: 20px 24px;
                    border-radius: 12px;
                    margin-bottom: 20px;
                }
                .header-box h1 { margin: 0 0 6px; font-size: 22px; font-weight: 700; }
                .header-box p { margin: 0; opacity: 0.92; font-size: 13px; }
                .meta-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                    margin-bottom: 20px;
                }
                .meta-item {
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    padding: 12px 14px;
                }
                .meta-label { font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: bold; }
                .meta-value { font-size: 16px; font-weight: bold; color: #0f172a; margin-top: 2px; }
                .overview-card {
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    border-radius: 10px;
                    padding: 18px 20px;
                    margin-bottom: 20px;
                }
                .section-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 24px 0 14px;
                    border-bottom: 2px solid #00a88f;
                    padding-bottom: 6px;
                }
            </style>
        </head>
        <body>
            <div class="header-box">
                <h1>WorkHub &ndash; AI Team Work Summary Report</h1>
                <p>Period: ${displayDateRange} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')}</p>
            </div>

            <div class="meta-grid">
                <div class="meta-item">
                    <div class="meta-label">Reporting Members</div>
                    <div class="meta-value">${members.length}</div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">Total Hours Logged</div>
                    <div class="meta-value">${totalLoggedHours} hrs</div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">Reporting Period</div>
                    <div class="meta-value" style="font-size: 13px;">${displayDateRange}</div>
                </div>
            </div>

            <div class="overview-card">
                <h2 style="margin: 0 0 10px; color: #1c4f86; font-size: 16px;">Executive Team Overview</h2>
                <p style="margin: 0; color: #334155; line-height: 1.6; font-size: 14px;">${summaryData.teamSummary || 'No summary available.'}</p>
                ${summaryData.keyHighlights && summaryData.keyHighlights.length ? `
                <div style="margin-top: 14px; padding-top: 12px; border-top: 1px solid #cbd5e1;">
                    <strong style="color: #0369a1; font-size: 13px;">Key Highlights &amp; Notable Outcomes:</strong>
                    <ul style="margin: 6px 0 0; padding-left: 20px; color: #334155; font-size: 12.5px;">
                        ${summaryData.keyHighlights.map(h => `<li style="margin-bottom: 3px;">${h}</li>`).join('')}
                    </ul>
                </div>
                ` : ''}
            </div>

            <div class="section-title">Individual Member Reports (${members.length})</div>
            ${memberHtml}
        </body>
        </html>
        `;
    };

    const handleDownloadDocx = () => {
        if (!summaryData) return;
        const htmlBody = generateReportHtml();
        const docContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
            <meta charset='utf-8'>
            <!--[if gte mso 9]>
            <xml>
                <w:WordDocument>
                    <w:View>Print</w:View>
                    <w:Zoom>100</w:Zoom>
                    <w:DoNotOptimizeForBrowser/>
                </w:WordDocument>
            </xml>
            <![endif]-->
            <style>
                body { font-family: 'Calibri', 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: #1e293b; line-height: 1.5; padding: 20px; }
                h1 { font-size: 18pt; color: #1c4f86; }
                h2 { font-size: 14pt; color: #0f172a; }
                h3 { font-size: 12pt; color: #1c4f86; }
                ul { margin-top: 4px; margin-bottom: 6px; }
                li { margin-bottom: 2px; }
            </style>
        </head>
        <body>
            ${htmlBody}
        </body>
        </html>
        `;

        const blob = new Blob(['\ufeff', docContent], {
            type: 'application/msword;charset=utf-8'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Team_Work_AI_Summary_${startDateStr || 'period'}_to_${endDateStr || 'period'}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleDownloadPdf = () => {
        if (!summaryData) return;
        const html = generateReportHtml();
        const printWindow = window.open('', '_blank', 'width=900,height=750');
        if (printWindow) {
            printWindow.document.open();
            printWindow.document.write(html);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
            }, 350);
        } else {
            window.print();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            className="wh-dialog wh-ai-summary-dialog"
            aria-labelledby="ai-summary-title"
        >
            {/* Dialog Header */}
            <div className="wh-dialog-head">
                <div className="wh-dialog-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AutoAwesomeRoundedIcon style={{ color: '#ffffff', fontSize: 24 }} />
                </div>
                <div className="wh-dialog-headings">
                    <h5 id="ai-summary-title">AI Team Work Summary</h5>
                    <p>{displayDateRange} · {summaryData ? `${(summaryData.members || []).length} reporting member${(summaryData.members || []).length === 1 ? '' : 's'} analyzed` : 'Powered by Groq LLM'}</p>
                </div>
                <IconButton className="wh-dialog-close" onClick={onClose} aria-label="Close">
                    <CloseRoundedIcon />
                </IconButton>
            </div>

            {/* Dialog Content */}
            <DialogContent dividers style={{ maxHeight: '72vh', overflowY: 'auto', background: '#f8fafc', padding: '20px' }}>
                {/* 1. Loading State */}
                {loading && (
                    <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                        <div style={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(28, 79, 134, 0.12), rgba(0, 168, 143, 0.15))',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 20,
                            boxShadow: '0 8px 24px -6px rgba(0, 168, 143, 0.3)'
                        }}>
                            <CircularProgress size={36} thickness={4} style={{ color: '#00a88f' }} />
                        </div>
                        <h4 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 8, fontFamily: 'Figtree, sans-serif' }}>
                            Analyzing Team Work Reports with AI
                        </h4>
                        <p style={{ fontSize: 14, color: '#64748b', maxWidth: 440, margin: '0 auto 20px', lineHeight: 1.5 }}>
                            Fetching daily status submissions from the database and synthesizing tasks, contributions, and progress for each team member...
                        </p>
                        <div style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            gap: 8,
                            textAlign: 'left',
                            background: '#ffffff',
                            padding: '14px 20px',
                            borderRadius: 14,
                            border: '1px solid #e2e8f0',
                            fontSize: 13,
                            color: '#475569'
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CheckCircleRoundedIcon style={{ fontSize: 16, color: '#00a88f' }} /> Fetching member entries from database
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <AutoAwesomeRoundedIcon style={{ fontSize: 16, color: '#2563eb' }} /> Processing deliverables via Groq LLM
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <StarRoundedIcon style={{ fontSize: 16, color: '#f59e0b' }} /> Generating individual contributions & observations
                            </span>
                        </div>
                    </div>
                )}

                {/* 2. Error State */}
                {!loading && errorMsg && (
                    <div style={{
                        padding: '30px 24px',
                        textAlign: 'center',
                        background: '#ffffff',
                        borderRadius: 16,
                        border: '1px solid #fecaca',
                        boxShadow: '0 2px 10px rgba(239, 68, 68, 0.05)'
                    }}>
                        <WarningAmberRoundedIcon style={{ fontSize: 44, color: '#ef4444', marginBottom: 12 }} />
                        <h4 style={{ fontSize: 17, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>
                            Unable to Generate AI Summary
                        </h4>
                        <p style={{ fontSize: 14, color: '#475569', maxWidth: 520, margin: '0 auto 16px', lineHeight: 1.5 }}>
                            {errorMsg}
                        </p>
                        {errorMsg.includes('GROQ_API_KEY') && (
                            <div style={{
                                maxWidth: 520,
                                margin: '0 auto 20px',
                                padding: '12px 16px',
                                background: '#fef2f2',
                                border: '1px dashed #f87171',
                                borderRadius: 10,
                                textAlign: 'left',
                                fontSize: 13,
                                color: '#7f1d1d'
                            }}>
                                <strong>Configuration Guide:</strong><br />
                                1. Open the project root <code>.env</code> file<br />
                                2. Add: <code>GROQ_API_KEY=your_groq_api_key_here</code><br />
                                3. Restart the backend server and try again.
                            </div>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<RefreshRoundedIcon />}
                            onClick={fetchSummary}
                            style={{
                                borderRadius: 999,
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #1c4f86, #00a88f)',
                                color: '#fff',
                                fontWeight: 600
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                )}

                {/* 3. Success State */}
                {!loading && !errorMsg && summaryData && (
                    <div>
                        {/* Top Navigation Bar: Section Tabs & Action Buttons */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            marginBottom: 18,
                            paddingBottom: 4
                        }}>
                            {/* Section Switcher Tabs */}
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                background: '#e2e8f0',
                                padding: '4px',
                                borderRadius: '12px'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('overview')}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '7px 18px',
                                        borderRadius: '9px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        fontFamily: 'Figtree, sans-serif',
                                        transition: 'all 0.2s ease',
                                        background: activeTab === 'overview' ? '#ffffff' : 'transparent',
                                        color: activeTab === 'overview' ? '#1c4f86' : '#64748b',
                                        boxShadow: activeTab === 'overview' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
                                    }}
                                >
                                    <GroupsRoundedIcon style={{ fontSize: 18, color: activeTab === 'overview' ? '#00a88f' : '#64748b' }} />
                                    Team Overview
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('individual')}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '7px 18px',
                                        borderRadius: '9px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        fontFamily: 'Figtree, sans-serif',
                                        transition: 'all 0.2s ease',
                                        background: activeTab === 'individual' ? '#ffffff' : 'transparent',
                                        color: activeTab === 'individual' ? '#1c4f86' : '#64748b',
                                        boxShadow: activeTab === 'individual' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
                                    }}
                                >
                                    <PersonRoundedIcon style={{ fontSize: 18, color: activeTab === 'individual' ? '#00a88f' : '#64748b' }} />
                                    Individual Reports
                                    <span style={{
                                        fontSize: '11px',
                                        padding: '2px 7px',
                                        borderRadius: '999px',
                                        background: activeTab === 'individual' ? '#e0f2fe' : '#cbd5e1',
                                        color: activeTab === 'individual' ? '#0369a1' : '#475569',
                                        fontWeight: 700
                                    }}>
                                        {summaryData?.members?.length || 0}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* TAB 1: TEAM OVERVIEW */}
                        {activeTab === 'overview' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {/* Executive Overview Card */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)',
                                    border: '1px solid #c7ddf5',
                                    borderRadius: 16,
                                    padding: '20px 24px',
                                    boxShadow: '0 2px 8px rgba(30, 41, 59, 0.04)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                        <AutoAwesomeRoundedIcon style={{ color: '#00a88f', fontSize: 22 }} />
                                        <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a', fontFamily: 'Figtree, sans-serif' }}>
                                            AI Executive Team Summary
                                        </h4>
                                    </div>
                                    <p style={{ margin: 0, fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
                                        {summaryData.teamSummary}
                                    </p>

                                    {/* Key Highlights Section */}
                                    {summaryData.keyHighlights && summaryData.keyHighlights.length > 0 && (
                                        <div style={{
                                            marginTop: 16,
                                            paddingTop: 16,
                                            borderTop: '1px solid #c7ddf5'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                                <StarRoundedIcon style={{ color: '#0284c7', fontSize: 18 }} />
                                                <h5 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: '#0f172a', fontFamily: 'Figtree, sans-serif' }}>
                                                    Key Highlights &amp; Notable Outcomes
                                                </h5>
                                            </div>
                                            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
                                                {summaryData.keyHighlights.map((point, idx) => (
                                                    <li key={idx} style={{ marginBottom: 4 }}>{point}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Summary KPI Metrics Grid */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                                    <div style={{
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 14,
                                        padding: '16px 18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 14
                                    }}>
                                        <div style={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 12,
                                            background: '#e0f2fe',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#0369a1'
                                        }}>
                                            <GroupsRoundedIcon style={{ fontSize: 24 }} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                                                Reporting Members
                                            </div>
                                            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', fontFamily: 'Figtree, sans-serif' }}>
                                                {summaryData?.members?.length || 0}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 14,
                                        padding: '16px 18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 14
                                    }}>
                                        <div style={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 12,
                                            background: '#dcfce7',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#15803d'
                                        }}>
                                            <AccessTimeRoundedIcon style={{ fontSize: 24 }} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                                                Total Hours Logged
                                            </div>
                                            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', fontFamily: 'Figtree, sans-serif' }}>
                                                {totalLoggedHours} hrs
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 14,
                                        padding: '16px 18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 14
                                    }}>
                                        <div style={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 12,
                                            background: '#fef3c7',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#b45309'
                                        }}>
                                            <CalendarMonthRoundedIcon style={{ fontSize: 24 }} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                                                Reporting Period
                                            </div>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', fontFamily: 'Figtree, sans-serif' }}>
                                                {displayDateRange}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB 2: INDIVIDUAL REPORTS */}
                        {activeTab === 'individual' && (
                            <div>
                                {/* Search Bar */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    marginBottom: 16
                                }}>
                                    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                                        Showing {filteredMembers.length} of {summaryData?.members?.length || 0} reporting members
                                    </span>
                                    <TextField
                                        size="small"
                                        placeholder="Search member, code, role…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        style={{ width: 280, background: '#ffffff', borderRadius: 12 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                                                </InputAdornment>
                                            ),
                                            style: { fontSize: 13 }
                                        }}
                                    />
                                </div>

                                {/* Member Summaries List */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    {filteredMembers.length === 0 ? (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '36px 16px',
                                            background: '#ffffff',
                                            borderRadius: 14,
                                            border: '1px dashed #cbd5e1',
                                            color: '#64748b',
                                            fontSize: 14
                                        }}>
                                            No reporting team members match your search criteria.
                                        </div>
                                    ) : (
                                        filteredMembers.map((member) => (
                                    <div
                                        key={member.employeeCode}
                                        style={{
                                            background: '#ffffff',
                                            borderRadius: 16,
                                            border: '1px solid #e2e8f0',
                                            boxShadow: '0 1px 4px rgba(15, 23, 42, 0.04)',
                                            overflow: 'hidden',
                                            transition: 'box-shadow 0.2s ease, border-color 0.2s ease'
                                        }}
                                    >
                                        {/* Member Header */}
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            padding: '14px 18px',
                                            background: member.hasReported ? '#ffffff' : '#fafafa',
                                            borderBottom: '1px solid #f1f5f9'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                {/* Avatar */}
                                                {member.profilePic ? (
                                                    <img
                                                        src={member.profilePic}
                                                        alt={member.employeeName}
                                                        style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: 42,
                                                        height: 42,
                                                        borderRadius: '50%',
                                                        background: member.hasReported
                                                            ? 'linear-gradient(135deg, #1c4f86, #00a88f)'
                                                            : '#94a3b8',
                                                        color: '#ffffff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 700,
                                                        fontSize: 15
                                                    }}>
                                                        {initials(member.employeeName)}
                                                    </div>
                                                )}

                                                {/* Info */}
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                                                            {member.employeeName}
                                                        </h4>
                                                        <span style={{
                                                            fontSize: 11.5,
                                                            fontWeight: 600,
                                                            color: '#64748b',
                                                            background: '#f1f5f9',
                                                            padding: '2px 8px',
                                                            borderRadius: 6
                                                        }}>
                                                            {member.employeeCode}
                                                        </span>
                                                    </div>
                                                    <span style={{ fontSize: 12.5, color: '#64748b' }}>
                                                        {member.designation || 'Team Member'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Badges / Hours */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {member.hasReported ? (
                                                    <>
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 5,
                                                            padding: '4px 10px',
                                                            borderRadius: 8,
                                                            background: '#e0f2fe',
                                                            color: '#0369a1',
                                                            fontSize: 12,
                                                            fontWeight: 600
                                                        }}>
                                                            <AccessTimeRoundedIcon style={{ fontSize: 15 }} />
                                                            {member.totalHours} hrs logged
                                                        </span>
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 5,
                                                            padding: '4px 10px',
                                                            borderRadius: 8,
                                                            background: '#dcfce7',
                                                            color: '#15803d',
                                                            fontSize: 12,
                                                            fontWeight: 600
                                                        }}>
                                                            <CalendarMonthRoundedIcon style={{ fontSize: 15 }} />
                                                            {member.daysReported} days
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: 8,
                                                        background: '#fef3c7',
                                                        color: '#b45309',
                                                        fontSize: 12,
                                                        fontWeight: 600
                                                    }}>
                                                        No reports submitted
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Member Body */}
                                        <div style={{ padding: '16px 18px' }}>
                                            {/* Executive Overview Highlight */}
                                            {member.executiveOverview && (
                                                <div style={{
                                                    fontStyle: 'italic',
                                                    fontSize: 13.5,
                                                    color: '#334155',
                                                    background: '#f8fafc',
                                                    padding: '10px 14px',
                                                    borderRadius: 10,
                                                    borderLeft: member.hasReported ? '3px solid #00a88f' : '3px solid #cbd5e1',
                                                    marginBottom: 14
                                                }}>
                                                    "{member.executiveOverview}"
                                                </div>
                                            )}

                                            {member.hasReported ? (
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
                                                    {/* 1. Tasks Completed */}
                                                    <div style={{
                                                        background: '#f0fdf4',
                                                        border: '1px solid #bbf7d0',
                                                        borderRadius: 12,
                                                        padding: '12px 14px'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                                            <CheckCircleRoundedIcon style={{ fontSize: 18, color: '#16a34a' }} />
                                                            <strong style={{ fontSize: 13, color: '#166534', fontFamily: 'Figtree, sans-serif' }}>
                                                                Tasks Completed
                                                            </strong>
                                                        </div>
                                                        {member.tasksCompleted && member.tasksCompleted.length > 0 ? (
                                                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#14532d', lineHeight: 1.5 }}>
                                                                {member.tasksCompleted.map((item, idx) => (
                                                                    <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p style={{ margin: 0, fontSize: 12, color: '#65a30d', fontStyle: 'italic' }}>
                                                                No specific completed tasks noted.
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* 2. Work / Progress Made */}
                                                    <div style={{
                                                        background: '#eff6ff',
                                                        border: '1px solid #bfdbfe',
                                                        borderRadius: 12,
                                                        padding: '12px 14px'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                                            <TrendingUpRoundedIcon style={{ fontSize: 18, color: '#2563eb' }} />
                                                            <strong style={{ fontSize: 13, color: '#1e40af', fontFamily: 'Figtree, sans-serif' }}>
                                                                Work / Progress Made
                                                            </strong>
                                                        </div>
                                                        {member.workInProgress && member.workInProgress.length > 0 ? (
                                                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#1e3a8a', lineHeight: 1.5 }}>
                                                                {member.workInProgress.map((item, idx) => (
                                                                    <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p style={{ margin: 0, fontSize: 12, color: '#60a5fa', fontStyle: 'italic' }}>
                                                                No ongoing progress items flagged.
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* 3. Important Contributions */}
                                                    <div style={{
                                                        background: '#fffbeb',
                                                        border: '1px solid #fde68a',
                                                        borderRadius: 12,
                                                        padding: '12px 14px'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                                            <StarRoundedIcon style={{ fontSize: 18, color: '#d97706' }} />
                                                            <strong style={{ fontSize: 13, color: '#92400e', fontFamily: 'Figtree, sans-serif' }}>
                                                                Important Contributions
                                                            </strong>
                                                        </div>
                                                        {member.importantContributions && member.importantContributions.length > 0 ? (
                                                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#78350f', lineHeight: 1.5 }}>
                                                                {member.importantContributions.map((item, idx) => (
                                                                    <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p style={{ margin: 0, fontSize: 12, color: '#d97706', fontStyle: 'italic' }}>
                                                                Steady ongoing execution.
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* 4. Relevant Observations */}
                                                    <div style={{
                                                        background: '#faf5ff',
                                                        border: '1px solid #e9d5ff',
                                                        borderRadius: 12,
                                                        padding: '12px 14px'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                                            <LightbulbRoundedIcon style={{ fontSize: 18, color: '#7e22ce' }} />
                                                            <strong style={{ fontSize: 13, color: '#6b21a8', fontFamily: 'Figtree, sans-serif' }}>
                                                                Relevant Observations
                                                            </strong>
                                                        </div>
                                                        {member.observations && member.observations.length > 0 ? (
                                                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#581c87', lineHeight: 1.5 }}>
                                                                {member.observations.map((item, idx) => (
                                                                    <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p style={{ margin: 0, fontSize: 12, color: '#a855f7', fontStyle: 'italic' }}>
                                                                Consistent logging recorded.
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{
                                                    padding: '12px 16px',
                                                    background: '#f8fafc',
                                                    borderRadius: 10,
                                                    fontSize: 13,
                                                    color: '#64748b'
                                                }}>
                                                    No work status reports were submitted by this team member during the period ({displayDateRange}).
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        )}
    </DialogContent>

            {/* Dialog Footer Actions */}
            <DialogActions style={{ padding: '14px 20px', background: '#ffffff', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 12.5, color: '#64748b' }}>
                    {summaryData ? (
                        <span>
                            <strong>{counts.reported}</strong> reporting member{counts.reported === 1 ? '' : 's'} analyzed
                        </span>
                    ) : null}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    {summaryData && (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<DownloadRoundedIcon />}
                                endIcon={<KeyboardArrowUpRoundedIcon />}
                                onClick={handleDownloadClick}
                                style={{
                                    borderRadius: 999,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #1c4f86, #00a88f)',
                                    color: '#ffffff',
                                    padding: '6px 18px'
                                }}
                            >
                                Download Report
                            </Button>
                            <Menu
                                anchorEl={downloadAnchorEl}
                                open={Boolean(downloadAnchorEl)}
                                onClose={handleDownloadClose}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                PaperProps={{
                                    style: {
                                        borderRadius: 14,
                                        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
                                        border: '1px solid #e2e8f0',
                                        minWidth: 200,
                                        padding: '4px',
                                        marginBottom: 8
                                    }
                                }}
                            >
                                <MenuItem
                                    disabled
                                    style={{ borderRadius: 8, margin: '2px 0', padding: '8px 12px', opacity: 0.55 }}
                                >
                                    <ListItemIcon style={{ minWidth: 32 }}>
                                        <PictureAsPdfRoundedIcon style={{ color: '#94a3b8', fontSize: 20 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="PDF Document"
                                        secondary="Coming soon"
                                        primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}
                                        secondaryTypographyProps={{ fontSize: 11, color: '#94a3b8' }}
                                    />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => { handleDownloadClose(); handleDownloadDocx(); }}
                                    style={{ borderRadius: 8, margin: '2px 0', padding: '8px 12px' }}
                                >
                                    <ListItemIcon style={{ minWidth: 32 }}>
                                        <ArticleRoundedIcon style={{ color: '#2563eb', fontSize: 20 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Word Document"
                                        secondary="Download as .docx"
                                        primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}
                                        secondaryTypographyProps={{ fontSize: 11, color: '#64748b' }}
                                    />
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        style={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: '#cbd5e1',
                            color: '#334155'
                        }}
                    >
                        Close
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default AISummaryModal;
